import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { Product, ProductInput, ProductList, ProductUpdate } from "@nh/shared";
import {
  archiveProduct,
  createProduct,
  getAdminProduct,
  listAdminProducts,
  updateProduct,
  type AdminProductsQuery,
} from "./products.api";

/**
 * Las llaves de caché del catálogo, en un solo sitio.
 *
 * Escribirlas a mano en cada hook es cómo se acaba con una mutación que
 * invalida `["products"]` y una consulta guardada bajo `["admin-products"]`:
 * todo compila, nada se refresca, y el producto recién creado no aparece
 * hasta recargar la página.
 */
export const adminProductKeys = {
  all: ["admin", "products"] as const,
  list: (query: AdminProductsQuery) => [...adminProductKeys.all, "list", query] as const,
  one: (id: string) => [...adminProductKeys.all, "one", id] as const,
};

export function useAdminProducts(
  query: AdminProductsQuery,
  options: { enabled?: boolean } = {},
): UseQueryResult<ProductList, Error> {
  return useQuery({
    queryKey: adminProductKeys.list(query),
    queryFn: () => listAdminProducts(query),
    enabled: options.enabled ?? true,
    /*
     * Al cambiar de página o de filtro, conservar la respuesta anterior
     * mientras llega la nueva: la tabla no se vacía ni salta a esqueleto
     * en cada tecleo del buscador.
     */
    placeholderData: (previous) => previous,
  });
}

export function useAdminProduct(
  id: string | undefined,
): UseQueryResult<Product, Error> {
  return useQuery({
    queryKey: adminProductKeys.one(id ?? ""),
    queryFn: () => getAdminProduct(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateProduct(): UseMutationResult<Product, Error, ProductInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductInput) => createProduct(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminProductKeys.all });
    },
  });
}

export function useUpdateProduct(
  id: string,
): UseMutationResult<Product, Error, ProductUpdate> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProductUpdate) => updateProduct(id, patch),
    onSuccess: (product) => {
      queryClient.setQueryData(adminProductKeys.one(id), product);
      void queryClient.invalidateQueries({ queryKey: adminProductKeys.all });
    },
  });
}

export function useArchiveProduct(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveProduct(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminProductKeys.all });
    },
  });
}
